import { 
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  User,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc,
  serverTimestamp,
  updateDoc,
  arrayUnion,
  FieldValue,
  Timestamp,
  query,
  collection,
  where,
  getDocs,
  increment
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { UserData, UserSubscription } from '../types/user';

// Sign in with Google
export const signInWithGoogle = async (): Promise<UserData | null> => {
  const provider = new GoogleAuthProvider();
  
  // Add these scopes for Google sign-in
  provider.addScope('https://www.googleapis.com/auth/userinfo.profile');
  provider.addScope('https://www.googleapis.com/auth/userinfo.email');
  
  // Set custom parameters
  provider.setCustomParameters({
    prompt: 'select_account'
  });
  
  try {
    console.log('Starting Google sign in...');
    
    // Set persistence to LOCAL
    await setPersistence(auth, browserLocalPersistence);
    console.log('Set persistence to LOCAL');
    
    const result = await signInWithPopup(auth, provider);
    console.log('Sign in successful:', result.user.email);
    const user = result.user;
    
    // Create or update user document in Firestore
    await syncUserData(user);
    console.log('User data synced');
    
    const userData = await getUserData(user.uid);
    console.log('User data retrieved:', userData);
    
    return userData;
  } catch (error: any) {
    console.error('Error signing in with Google:', error);
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign in cancelled by user');
    }
    throw error;
  }
};

// Sign out
export const signOutUser = async () => {
  try {
    await signOut(auth);
    console.log('User signed out successfully');
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

interface FirestoreUserData extends Omit<UserData, 'createdAt' | 'updatedAt' | 'lastLogin'> {
  createdAt: FieldValue | Timestamp;
  updatedAt: FieldValue | Timestamp;
  lastLogin: FieldValue | Timestamp;
}

// Create or update user in Firestore
export const syncUserData = async (user: User): Promise<void> => {
  if (!user.uid) {
    console.error('No user ID provided');
    return;
  }

  const userRef = doc(db, 'users', user.uid);

  try {
    console.log('Syncing user data for:', user.uid);
    const docSnap = await getDoc(userRef);
    
    if (!docSnap.exists()) {
      // New user - create with all required fields
      const newUserData = {
        uid: user.uid,
        email: user.email || '',
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        updatedAt: serverTimestamp(),
        subscriptionStatus: 'free' as const,
        interview_quota: 0,
        has_purchased_premium: false,
        settings: {
          notifications: true,
          emailUpdates: true
        }
      } as const;

      console.log('Creating new user with data:', newUserData);
      await setDoc(userRef, newUserData);
      console.log('Created new user document');
    } else {
      // Existing user - only update auth-related fields
      const updateData = {
        email: user.email || '',
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        lastLogin: serverTimestamp(),
        updatedAt: serverTimestamp()
      } as const;
      
      console.log('Updating existing user with data:', updateData);
      await updateDoc(userRef, updateData);
      console.log('Updated existing user document');
    }
  } catch (error) {
    console.error('Error syncing user data:', error);
    throw error;
  }
};

// Get user data from Firestore
export const getUserData = async (uid: string): Promise<UserData | null> => {
  if (!uid) {
    console.error('No user ID provided');
    return null;
  }

  try {
    console.log('Getting user data for:', uid);
    const userRef = doc(db, 'users', uid);
    const docSnap = await getDoc(userRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      console.log('Retrieved user data:', data);
      return {
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        lastLogin: data.lastLogin?.toDate() || new Date()
      } as UserData;
    }
    console.log('No user document found');
    return null;
  } catch (error) {
    console.error('Error getting user data:', error);
    throw error;
  }
};

// Update user subscription
export const updateUserSubscription = async (
  uid: string,
  subscription: Partial<UserSubscription>
) => {
  try {
    const userRef = doc(db, 'users', uid);
    
    await updateDoc(userRef, {
      subscriptionStatus: subscription.status,
      interview_quota: subscription.interview_quota,
      has_purchased_premium: subscription.has_purchased_premium,
      'subscription.features': subscription.features,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error updating user subscription:', error);
    throw error;
  }
};

// Add interview session to user's history
export const addInterviewSession = async (
  uid: string, 
  sessionId: string
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      interview_sessions: arrayUnion(sessionId),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error adding interview session:', error);
    throw error;
  }
};

// Mark interview as completed (without feedback)
export const markInterviewCompleted = async (
  uid: string, 
  sessionId: string
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      completed_interviews: arrayUnion(sessionId),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking interview as completed:', error);
    throw error;
  }
};

// Simplified interview counting function
export const getInterviewCounts = async (uid: string): Promise<{completed: number, remaining: number}> => {
  try {
    // Get user's quota
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    const quota = userDoc.data().interview_quota || 0;
    
    // Count interviews with has_feedback === true
    const interviewsQuery = query(
      collection(db, 'interview-sessions'),
      where('uid', '==', uid),
      where('has_feedback', '==', true)
    );
    
    const querySnapshot = await getDocs(interviewsQuery);
    const completedCount = querySnapshot.size;
    const remainingCount = Math.max(0, quota - completedCount);
    
    console.log(`User ${uid} counts: ${completedCount} completed, ${remainingCount} remaining (quota: ${quota})`);
    
    return {
      completed: completedCount,
      remaining: remainingCount
    };
  } catch (error) {
    console.error('Error getting interview counts:', error);
    throw error;
  }
};

// Simple function to update interview quota
export const updateInterviewQuota = async (
  uid: string,
  addQuota: number
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      interview_quota: increment(addQuota),
      updatedAt: serverTimestamp()
    });
    
    console.log(`Updated interview quota for user ${uid}: added ${addQuota}`);
  } catch (error) {
    console.error('Error updating interview quota:', error);
    throw error;
  }
};

// Subscribe to auth state changes
export const subscribeToAuthChanges = (callback: (user: User | null) => void) => {
  console.log('Setting up auth state listener');
  return onAuthStateChanged(auth, (user) => {
    console.log('Auth state changed:', user ? user.email : 'No user');
    callback(user);
  });
}; 